import React, { useEffect } from 'react';

import bg1 from '/assets/backgrounds/1.png';

import BackgroundRotator from './generic/BackgroundRotator';
import { Box, Button, Dialog, DialogContent, IconButton, Paper, Typography } from '@mui/material';
import useNotificationStore from '@gui/store/notification';
import { LoadingStatus, setLoadingStatus } from 'store/LoadingStore';
import useViewerStore from 'store/viewerStore';
import { getSentence } from '@gui/constants/sentences';
import { Announcement03, InfoCircle } from '@untitledui-pro/icons/line';
import CustomTooltip from './generic/CustomTooltip';
import usePersistentStore, { setPersistenceEnabled } from '@gui/store/persistent/persistentStore';
import { getViewer } from 'store/viewerStore';
import { useErrorScreenStore } from '../store/errorScreenStore';
import { ControllerManager } from '@gui/controller/ControllerManager';
import ReportIssue from './panels/settings/ReportIssue';

const backgrounds = [bg1];

const ErrorScreen: React.FC<{ message?: string; stack?: string | null; onRetry?: () => void }> = ({ message, stack, onRetry }) => {
    const notificationMessage = useNotificationStore((s) => s.notifications.at(-1)?.message);
    const errorMessage = message || notificationMessage;
    const refreshViewer = useViewerStore().refreshViewer;
    const storageName = usePersistentStore.persist.getOptions().name;
    const [disableRetry, setDisableRetry] = React.useState(false);
    const setErrorScreenVisible = useErrorScreenStore((s) => s.setErrorScreenVisible);
    const [reportIssueOpen, setReportIssueOpen] = React.useState(false);

    useEffect(() => {
        setErrorScreenVisible(true);
        setPersistenceEnabled(false);
        return () => {
            setErrorScreenVisible(false);
            setPersistenceEnabled(true);
        };
    }, [setErrorScreenVisible, setPersistenceEnabled]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem('ErrorRaised');
            if (!raw) return;
            const parsed = JSON.parse(raw) as { message?: string; currentTs?: number };
            if (!parsed?.message || !parsed?.currentTs) return;
            const isSameError = parsed.message === errorMessage;
            const isRecent = Date.now() - parsed.currentTs < 2000;
            if (isSameError && isRecent) {
                setDisableRetry(true);
            }
        } catch (e) {
            console.warn('Could not read ErrorRaised state', e);
        }
    }, [errorMessage]);

    const markErrorRaised = () => {
        try {
            localStorage.setItem(
                'ErrorRaised',
                JSON.stringify({
                    message: errorMessage,
                    currentTs: Date.now(),
                })
            );
        } catch (e) {
            console.warn('Could not persist retry metadata', e);
        }
    };

    const refreshGui = () => {
        if (onRetry) {
            onRetry(); // clears error boundary
        }
        refreshViewer(); // re-instantiates Visualization + initialize
        setLoadingStatus(LoadingStatus.VISUALIZER_READY);
        getViewer().ensureCanvasAttached();
    };

    const handleReload = async () => {
        markErrorRaised();
        const restored: boolean = await ControllerManager.getInstance().getStateController().restoreAutoSaveState(storageName);
        if (restored) {
            await usePersistentStore.persist.rehydrate();
        }
        window.location.reload();
    };

    const handleReset = async () => {
        markErrorRaised();
        const restored: boolean = await ControllerManager.getInstance().getStateController().restoreAutoSaveState(storageName);
        if (restored) {
            await usePersistentStore.persist.rehydrate();
        }
        refreshGui();
    };

    const handleResetLocalStorageAndReload = async () => {
        await ControllerManager.getInstance().getStateController().deleteAutomaticState();
        window.location.reload();
    };
    return (
        <div className="loading-screen">
            <BackgroundRotator images={backgrounds} />
            <div className="loading-screen-content">
                <Paper elevation={0} className="loading-card" sx={{ top: '100px' }}>
                    <Box className="loading-card-content" sx={{ gap: 2 }}>
                        <Box className="loading-card-header" sx={{ alignItems: 'stretch', justifyContent: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    width: '100%',
                                }}
                            >
                                <CustomTooltip
                                    mode="hover"
                                    placement="bottom"
                                    popperSx={{ zIndex: 12000, transformOrigin: 'top center' }}
                                    PopperProps={{
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: { offset: [0, 12] },
                                            },
                                        ],
                                    }}
                                    title={<Box>{'Report issue'}</Box>}
                                >
                                    <IconButton
                                        size="small"
                                        aria-label="Report issue"
                                        onClick={() => setReportIssueOpen(true)}
                                        className="loading-project-button"
                                        sx={{ color: 'rgb(214, 40, 47)' }}
                                    >
                                        <Announcement03 className="icon-size-big" />
                                    </IconButton>
                                </CustomTooltip>
                            </Box>
                            <Box className="loading-card-header-text" sx={{ textAlign: 'center', width: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    <Typography variant="h4" className="loading-title" color="rgb(214, 40, 47)">
                                        {errorMessage}
                                    </Typography>
                                    {stack ? (
                                        <CustomTooltip
                                            mode="hover"
                                            placement="bottom"
                                            popperSx={{ zIndex: 12000, transformOrigin: 'top center' }}
                                            PopperProps={{
                                                modifiers: [
                                                    {
                                                        name: 'offset',
                                                        options: { offset: [0, 12] },
                                                    },
                                                ],
                                            }}
                                            title={
                                                <Box
                                                    sx={{
                                                        maxWidth: 'min(90vw, 900px)',
                                                        minWidth: 520,
                                                        maxHeight: 420,
                                                        overflow: 'auto',
                                                        whiteSpace: 'pre-wrap',
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.8rem',
                                                    }}
                                                >
                                                    {stack}
                                                </Box>
                                            }
                                        >
                                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                <InfoCircle color="red" className="icon-size-medium" />
                                            </span>
                                        </CustomTooltip>
                                    ) : null}
                                </Box>
                                <Typography
                                    variant="body1"
                                    className="loading-subtitle"
                                    sx={{ fontSize: '0.9rem', mt: 1, lineHeight: 1.5 }}
                                >
                                    {getSentence('ErrorScreen.Subtitle.Message')}
                                </Typography>
                            </Box>
                        </Box>
                        <Box className="error-screen-actions">
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={disableRetry}
                                className="error-screen-button"
                                onClick={refreshGui}
                            >
                                Try to continue
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={disableRetry}
                                className="error-screen-button error-screen-button--restore"
                                onClick={handleReset}
                            >
                                Restore previous snapshot and continue
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={disableRetry}
                                className="error-screen-button error-screen-button--reload"
                                onClick={handleReload}
                            >
                                Restore previous snapshot and reload
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                className="error-screen-button error-screen-button--hardreset"
                                onClick={handleResetLocalStorageAndReload}
                            >
                                Reset local storage and reload
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </div>
            <Dialog open={reportIssueOpen} onClose={() => setReportIssueOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: 11000 }}>
                <DialogContent>
                    <ReportIssue showContent={reportIssueOpen} onFeedbackSent={() => setReportIssueOpen(false)} />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ErrorScreen;
